import * as React from 'react';
import {AIAnalyseDescription} from "./Helper/AIAnalyseDescription";
import {Dispatch, useRef, useState} from "react";
import {PhotoUpload} from "./Helper/PhotoUpload";
import {NotPossibleIdentifyTrashes} from "./Helper/NotPossibleIdentifyTrashes";
import {BagsDetected} from "./Helper/BagsDetected";
import {Modal} from "./Helper/Modal";
import {TooHeavy} from "./Helper/TooHeavy";
import {AnimatedVan} from "./Helper/AnimatedVan";

type TrashSelectionModel = {
    setStep: Dispatch<any>;
    manualSelection: boolean;
    setManualSelection: Dispatch<any>;
    manualTrashType: string;
    setManualTrashType: Dispatch<any>;
    manualTrashSize: string;
    setManualTrashSize: Dispatch<any>;
    manualTrashPrice: string;
    setManualTrashPrice: Dispatch<any>;
    trashesPhotos: any;
    setTrashesPhotos: Dispatch<any>;
    trashStacksCount: number;
    setTrashStacksCount: Dispatch<any>;
    maxTrashStacksCount: number;
    tags: any[];
    setTags: Dispatch<any>;
    selectedSize: string;
    setSelectedSize: Dispatch<any>;
    apiUrl: string;
    setUploadedPhotos: Dispatch<any>;
    setShowLoader: Dispatch<any>;
    showLoader: string;
    setExtraInformationAboutTrashes: Dispatch<any>;
    selectedCity: string;
    setSelectedCity: Dispatch<any>;
    setBagsCount: Dispatch<any>;
    stepsBarRef: any;
};

interface TrashType {
    trashTypeId: number;
    sizes: Size[];
    name: string;
    tags: string[];
    description: string;
}

interface Size {
    trashSizeId: number;
    size: string;
    price: number;
}

function fetchGarbageCollectionPackages(apiUrl: string, signal: AbortSignal) {
    return new Promise<{
        trashTypes: TrashType[]
    }>((resolve, reject) => {
        const options = {
            method: 'GET',
            headers: {
                'X-Requested-With': 'XMLHttpRequest'
            },
            signal
        };
        fetch(apiUrl + 'garbage-collection-packages', options)
            .then((res) => {
                if (!res.ok) {
                    throw new Error(`HTTP error! status: ${res.status}`);
                }
                return res.json();
            })
            .then(({trashTypes}) => {
                resolve({trashTypes});
            })
            .catch(reject);
    });
}

export const TrashSelectionStep = ({
                                       setStep,
                                       manualSelection,
                                       setManualSelection,
                                       manualTrashType,
                                       setManualTrashType,
                                       manualTrashSize,
                                       setManualTrashSize,
                                       manualTrashPrice,
                                       setManualTrashPrice,
                                       trashesPhotos,
                                       setTrashesPhotos,
                                       trashStacksCount,
                                       setTrashStacksCount,
                                       maxTrashStacksCount,
                                       tags,
                                       setTags,
                                       selectedSize,
                                       setSelectedSize,
                                       apiUrl,
                                       setUploadedPhotos,
                                       setShowLoader,
                                       showLoader,
                                       setExtraInformationAboutTrashes,
                                       selectedCity,
                                       setSelectedCity,
                                       setBagsCount,
                                       stepsBarRef
                                   }: TrashSelectionModel) => {

    const requestAbortController = React.useRef<AbortController | null>(null);
    const [trashTypeList, setTrashTypeList] = useState([]);
    const [trashInformationError, setTrashInformationError] = useState(false);
    const [cityError, setCityError] = useState(false);
    const [priceLoaded, setPriceLoaded] = useState(false);
    const ref = useRef(null);
    const photoUploadSection = useRef(null);
    const manualSelectionBlock = useRef(null);
    const doPhotoAnalyses = useRef(null);
    const calculatedPrice = useRef(null);
    const [showNotPossibleIdentifyTrashes, setShowNotPossibleIdentifyTrashes] = useState(false);
    const [tooHeavy, setTooHeavy] = useState(false);
    const [bagsDetected, setBagsDetected] = useState(false);
    const [showModalOn, setShowModal] = useState(false);
    const [modalText, setModalText] = useState('');

    const handleManualSelectionClick = () => {
        setManualSelection(!manualSelection);
        setManualTrashType('');
        setManualTrashPrice('');
        setManualTrashSize('');
        setTags([]);
        setSelectedSize('');

        setTimeout(() => {
            manualSelectionBlock.current?.scrollIntoView({behavior: 'smooth'});
        }, 100);

    };

    const handleManualTrashSelection = (event: any) => {
        let valid = false;

        if (selectedCity !== '') {
            setCityError(false);
            valid = true;
        } else {
            setCityError(true);
        }

        if (valid === false) {
            ref.current?.scrollIntoView({behavior: 'smooth'});
        }

        if (valid === true) {
            setManualTrashType(event.target.value);
            setManualTrashPrice('');
            setManualTrashSize('');
            setTags([]);
            setSelectedSize('');
        }
    };

    const addAnotherTrashStack = () => {
        setTrashStacksCount(prev => prev + 1);
    };

    const handleManualTrashSizeSelection = async (event: any) => {
        let valid = false;

        if (selectedCity !== '') {
            setCityError(false);
            valid = true;
        } else {
            setCityError(true);
        }

        if (valid === false) {
            ref.current?.scrollIntoView({behavior: 'smooth'});
        }

        if (valid === true) {
            setManualTrashSize(event.target.value);

            const selectedType = trashTypeList.find(item => item.trashTypeId == manualTrashType);

            if (!selectedType) {
                return;
            }

            const selectedTypeSize = selectedType.sizes.find(size => size.trashSizeId == event.target.value);

            if (selectedTypeSize) {
                setSelectedSize(selectedTypeSize.size);
            }

            setTags(selectedType.tags);

            try {
                const response = await fetch(apiUrl + "price/" + encodeURIComponent(manualTrashType) + "/" + encodeURIComponent(event.target.value) + "/" + encodeURIComponent(selectedCity), {
                    method: "GET"
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (data.price) {
                    setManualTrashPrice(data.price);
                    setTimeout(() => {
                        calculatedPrice.current?.scrollIntoView({behavior: "smooth"});
                    }, 100);
                }
            } catch (error) {
                console.error(error);
            }
        }
    };

    const handleNextClick = () => {
        let valid = false;
        if (selectedCity !== '') {
            setCityError(false);
            valid = true;
        } else {
            setCityError(true);
        }

        if (manualTrashPrice !== '') {
            setTrashInformationError(false);
        } else {
            valid = false;
            setTrashInformationError(true);
        }

        if (valid === false) {
            ref.current?.scrollIntoView({behavior: 'smooth'});
        }

        if (valid === true) {
            setStep(2);
            stepsBarRef.current?.scrollIntoView({behavior: 'smooth'});
        }
    };

    const showModal = (text: string) => {
        setShowModal(true);
        setModalText(text);
    };

    const fetchAddressOptions = () => {
        const controller = new AbortController();
        fetchGarbageCollectionPackages(apiUrl, controller.signal)
            .then(({trashTypes}) => {
                setTrashTypeList(trashTypes);
            })
            .catch((error) => {
                if (error.name !== 'AbortError') {
                    console.error(error);
                }
            });

        requestAbortController.current = controller;
    };


    const analyzePhotos = async () => {
        let valid = false;

        if (selectedCity !== '') {
            setCityError(false);
            valid = true;
        } else {
            setCityError(true);
        }

        if (valid === false) {
            ref.current?.scrollIntoView({behavior: 'smooth'});
        }

        if (valid) {
            setPriceLoaded(false);
            const formData = new FormData();
            setShowLoader('show');
            trashesPhotos.forEach((group, groupIndex) => {
                group.forEach(item => {
                    formData.append(`trashesPhotos[${groupIndex}][]`, item.file);
                });
            });

            formData.append('city', selectedCity);

            try {
                const response = await fetch(apiUrl + "garbage-analyze", {
                    method: "POST",
                    body: formData
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const data = await response.json();

                if (data.price && data.trashTypeId && data.trashSizeId && data.size && data.tags) {
                    if (data.tooHeavy === false) {
                        setManualTrashPrice(data.price)
                        setManualTrashType(data.trashTypeId);
                        setManualTrashSize(data.trashSizeId);
                        setSelectedSize(data.size);
                        setTags(data.tags);
                        setUploadedPhotos(data.uploadedFiles);
                        if (data.bagsCount > 0) {
                            setBagsDetected(true);
                            setBagsCount(data.bagsCount);
                        }

                        setTimeout(() => {
                            calculatedPrice.current?.scrollIntoView({behavior: "smooth"});
                        }, 100);

                    } else {
                        setTooHeavy(true);
                    }
                } else {
                    setShowNotPossibleIdentifyTrashes(true);
                    setManualTrashPrice('')
                    setManualTrashType('');
                    setManualTrashSize('');
                    setSelectedSize('');
                    setTags([]);
                    setUploadedPhotos([]);
                }
            } catch (error) {
                console.error(error);
                setShowNotPossibleIdentifyTrashes(true);
            } finally {
                setShowLoader('hide');
            }
        }
    }

    React.useEffect(() => {
        fetchAddressOptions();
        // abort request on unmount
        return () => requestAbortController.current?.abort();
    }, []);

    return (
        <>
            <span ref={ref} className="invisible"></span>
            <AIAnalyseDescription selectedCity={selectedCity}
                                  setSelectedCity={setSelectedCity}
                                  cityError={cityError}
                                  setCityError={setCityError}
                                  setManualTrashSize={setManualTrashSize}
                                  setManualTrashPrice={setManualTrashPrice}
                                  photoUploadSection={photoUploadSection}
                                  apiUrl={apiUrl}
            />
            <div ref={photoUploadSection} id="upload-photo-section" className="grid">
                <div className="col-70" style={trashInformationError ? {borderColor: "red"} : {}}>
                    <div>
                        {Array.from({length: trashStacksCount}).map((_, index) => (
                            <PhotoUpload setTrashesPhotos={setTrashesPhotos}
                                         trashesPhotos={trashesPhotos}
                                         index={index}
                                         key={index}
                                         showLoader={showLoader}
                                         handleNextClick={handleNextClick}
                                         selectedCity={selectedCity}
                                         doPhotoAnalyses={doPhotoAnalyses}
                                         setManualTrashPrice={setManualTrashPrice}
                            />
                        ))}
                    </div>

                    <div>
                        <div className="actions-card">
                            <button className="primary" disabled={trashesPhotos.length === 0} onClick={analyzePhotos}>✧
                                Analizuoti krūvą
                            </button>
                            {maxTrashStacksCount > trashStacksCount && (
                                <button className="secondary" type="button" onClick={addAnotherTrashStack}>+ Pridėti
                                    kitą atliekų
                                    krūvą</button>)}
                            {manualSelection == false && (
                                <a onClick={handleManualSelectionClick}>Neturiu
                                    nuotraukų, suvesiu rankiniu būdu</a>)}
                            {manualSelection && (<a onClick={handleManualSelectionClick}>Slėpti
                                rankinį įvedimą</a>)}
                        </div>
                    </div>
                    <span ref={manualSelectionBlock} className="invisible"></span>
                    {manualSelection && (<div>
                        <div id="manual-selection">
                            <div id="manual-selection-block">
                                <h3>Rankinis atliekų suvedimas</h3>
                                <p>Pasirinkite atliekų tipą ir apytikslį kiekį.</p>
                                <h4>Atliekų tipas</h4>
                                <div className="waste-options">
                                    {trashTypeList.map(function (item) {
                                            return <label key={item.trashTypeId} className="waste-card"
                                                          onClick={handleManualTrashSelection}><input
                                                type="radio" name="trash-type"
                                                value={item.trashTypeId}
                                                checked={item.trashTypeId == manualTrashType}/><span>{item.name}</span>
                                                <button className="info-btn" type="button"
                                                        onClick={() => showModal(item.description)}>i
                                                </button>
                                            </label>
                                        }
                                    )}
                                </div>
                                {manualTrashType != '' && (
                                    <><h4 className="clearfix">Kiekis</h4>

                                        <div className="quantity-options">
                                            {trashTypeList
                                                .filter(item => item.trashTypeId == manualTrashType)
                                                .flatMap(item =>
                                                    item.sizes.map((size, index) => (

                                                        <button key={index} type="button"
                                                                className={manualTrashSize == size.trashSizeId ? "quantity-btn selected" : "quantity-btn"}
                                                                value={size.trashSizeId} id={"size" + size.trashSizeId}
                                                                onClick={handleManualTrashSizeSelection}>{size.size}
                                                        </button>
                                                    ))
                                                )}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>)}
                </div>
                <span ref={calculatedPrice} className="invisible"></span>
                {manualTrashPrice !== '' && (
                    <div className="col-30">
                        <div>
                            <h3>Atliekų kiekis</h3>
                            {manualSelection === false && tags && tags.length > 0 && (<div className="kpi-grid">
                                    <div className="kpi">
                                        <div className="analysis-status-line">
                                            <span className="analysis-status-dot"></span>
                                            <div className="analysis-status-copy"><span
                                                className="analysis-status-text">AI analizė baigta</span>
                                            </div>
                                        </div>
                                        <strong>Aptiktos atliekos</strong>
                                        <div className="tags">
                                            {tags.map(function (tag, index) {
                                                return <div key={index}>{tag}</div>
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div className="kpi-grid">
                                <div className="kpi">
                                    <div className="kpi-label">Mikroautobuso užpildymas</div>
                                    <div className="kpi-value"
                                         id="volume">{selectedSize !== '' ? '~' + selectedSize : '---'}</div>
                                    <div className="kpi-sub">Pagal matomą atliekų tūrį</div>
                                </div>
                            </div>
                            <div className="truck-zone">
                                <div className="truck-stage">
                                    <div className="route-line"></div>
                                    <div className="route-dot one"></div>
                                    <div className="route-dot two"></div>
                                    <AnimatedVan selectedSize={selectedSize}/>
                                </div>
                            </div>
                            <div className="clearfix"></div>
                            {manualTrashPrice !== '' && (<div className="card">
                                <div className="stats">
                                    <div className="stat">
                                        <div className="label">Preliminari kaina</div>
                                        <div className="stat-value green ready" id="price">{manualTrashPrice}€</div>
                                    </div>
                                    <div className="stat">
                                        <div className="label">Transportas</div>
                                        <div className="stat-value ready" id="amountValue">Mikroautobusas</div>
                                    </div>
                                </div>
                                <div className="notice">
                                    <div className="notice-text">
                                        <strong>Galutinė kaina</strong> gali būti patikslinta vietoje pagal atliekų
                                        kiekį ir rūšį.
                                    </div>
                                </div>
                            </div>)}
                        </div>
                        <div className="navigation-block navigation-block-price">
                            <button className="btn primary-btn btn-normal btn-full btn-right"
                                    onClick={handleNextClick}>Tęsti užsakymą →
                            </button>
                        </div>
                    </div>
                )}

                {manualTrashPrice === '' && (
                    <div className="col-30">
                        <div className="results-placeholder" id="resultsPlaceholder">
                            <div className="placeholder-icon">🚚</div>
                            <h2>Rezultatas atsiras čia</h2>
                            <p>Įkelkite nuotrauką arba suveskite atliekas rankiniu būdu — tada parodysime kiekį, kainą
                                ir mikroautobuso užpildymą.</p>
                        </div>
                    </div>
                )}
            </div>

            <NotPossibleIdentifyTrashes showNotPossibleIdentifyTrashes={showNotPossibleIdentifyTrashes}
                                        setShowNotPossibleIdentifyTrashes={setShowNotPossibleIdentifyTrashes}/>

            <TooHeavy tooHeavy={tooHeavy}
                      setTooHeavy={setTooHeavy}/>

            <BagsDetected bagsDetected={bagsDetected}
                          setBagsDetected={setBagsDetected}
                          setExtraInformationAboutTrashes={setExtraInformationAboutTrashes}
                          calculatedPrice={calculatedPrice}
                          setManualTrashPrice={setManualTrashPrice}
                          apiUrl={apiUrl}
                          selectedSize={selectedSize}
                          city={selectedCity}
                          setTooHeavy={setTooHeavy}
                          setBagsCount={setBagsCount}
            />

            <Modal text={modalText} showModal={showModalOn} setShowModal={setShowModal}/>
        </>
    )
        ;
}

